import React from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import {
    FiPieChart,
    FiUsers,
    FiShield,
    FiLayers,
    FiRepeat,
    FiCalendar,
    FiLogOut,
    FiMessageSquare,
    FiGrid,
    FiImage
} from 'react-icons/fi';
import { useAuth } from '../auth/AuthContext';
import NotificationBell from '../notifications/NotificationBell';
import DoctorList from '../doctors/DoctorList';
import CreateDoctor from '../doctors/CreateDoctor';
import EditDoctor from '../doctors/EditDoctor';
import GuildList from '../guilds/GuildList';
import CreateGuild from '../guilds/CreateGuild';
import CoordinatorList from '../coordinators/CoordinatorList';
import CreateCoordinator from '../coordinators/CreateCoordinator';
import EditCoordinator from '../coordinators/EditCoordinator';
import ReferralList from '../referrals/ReferralList';
import EventList from '../events/EventList';
import EventForm from '../events/EventForm';
import TestimonialList from '../testimonials/TestimonialList';
import TaxonomyManagement from '../specialties/TaxonomyManagement';
import BannerManagement from '../banners/BannerManagement';
import IncomingAppointments from '../appointments/IncomingAppointments';
import LegalSettings from './LegalSettings';
import { getAllDoctors } from '../doctors/doctorService';
import { getAllCoordinators } from '../coordinators/coordinatorService';
import { getAllGuilds } from '../guilds/guildService';
import { getAllReferrals } from '../referrals/referralService';
import './Dashboard.css';

export default function Dashboard() {
    const { currentUser, signOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    async function handleSignOut() {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    }

    const isActive = (path) => location.pathname === path;

    return (
        <div className="dashboard-layout admin-theme">
            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <h2>AssistHealth</h2>
                    <p>Guild Lite Admin</p>
                </div>

                <nav className="sidebar-nav">
                    <Link to="/dashboard" className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiPieChart /></span>
                        <span className="nav-text">Overview</span>
                    </Link>

                    <Link to="/dashboard/doctors" className={`nav-item ${location.pathname.startsWith('/dashboard/doctors') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiUsers /></span>
                        <span className="nav-text">Doctors</span>
                    </Link>
                    <Link to="/dashboard/coordinators" className={`nav-item ${location.pathname.startsWith('/dashboard/coordinators') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiShield /></span>
                        <span className="nav-text">Coordinators</span>
                    </Link>
                    <Link to="/dashboard/guilds" className={`nav-item ${location.pathname.startsWith('/dashboard/guilds') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiLayers /></span>
                        <span className="nav-text">Medical Guilds</span>
                    </Link>
                    <Link to="/dashboard/referrals" className={`nav-item ${location.pathname.startsWith('/dashboard/referrals') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiRepeat /></span>
                        <span className="nav-text">Patient Referrals</span>
                    </Link>
                    <Link to="/dashboard/appointments" className={`nav-item ${location.pathname.startsWith('/dashboard/appointments') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiCalendar /></span>
                        <span className="nav-text">Appointments</span>
                    </Link>

                    <Link to="/dashboard/events" className={`nav-item ${location.pathname.startsWith('/dashboard/events') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiCalendar /></span>
                        <span className="nav-text">Events</span>
                    </Link>
                    <Link to="/dashboard/testimonials" className={`nav-item ${location.pathname.startsWith('/dashboard/testimonials') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiMessageSquare /></span>
                        <span className="nav-text">Testimonials</span>
                    </Link>
                    <Link to="/dashboard/taxonomy" className={`nav-item ${location.pathname.startsWith('/dashboard/taxonomy') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiGrid /></span>
                        <span className="nav-text">Medical Categories</span>
                    </Link>
                    <Link to="/dashboard/banners" className={`nav-item ${location.pathname.startsWith('/dashboard/banners') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiImage /></span>
                        <span className="nav-text">Ad Banners</span>
                    </Link>
                    <Link to="/dashboard/legal" className={`nav-item ${location.pathname.startsWith('/dashboard/legal') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiShield /></span>
                        <span className="nav-text">Legal Settings</span>
                    </Link>
                </nav>

                <div className="sidebar-footer">
                    <button onClick={handleSignOut} className="signout-button">
                        <span className="nav-icon"><FiLogOut /></span>
                        <span className="nav-text">Sign Out</span>
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <main className="main-content">
                <header className="main-header">
                    <div className="header-left">
                    </div>
                    <div className="header-right">
                        <NotificationBell userId={currentUser?.uid} role="admin" />
                    </div>
                </header>

                <Routes>
                    <Route path="/" element={<Overview />} />
                    <Route path="/doctors" element={<DoctorList />} />
                    <Route path="/doctors/create" element={<CreateDoctor />} />
                    <Route path="/doctors/edit/:id" element={<EditDoctor />} />
                    <Route path="/guilds" element={<GuildList />} />
                    <Route path="/guilds/create" element={<CreateGuild />} />
                    <Route path="/coordinators" element={<CoordinatorList />} />
                    <Route path="/coordinators/create" element={<CreateCoordinator />} />
                    <Route path="/coordinators/edit/:id" element={<EditCoordinator />} />
                    <Route path="/referrals" element={<ReferralList />} />
                    <Route path="/events" element={<EventList />} />
                    <Route path="/events/create" element={<EventForm />} />
                    <Route path="/events/edit/:id" element={<EventForm />} />
                    <Route path="/testimonials" element={<TestimonialList />} />
                    <Route path="/taxonomy" element={<TaxonomyManagement />} />
                    <Route path="/banners" element={<BannerManagement />} />
                    <Route path="/appointments" element={<IncomingAppointments />} />
                    <Route path="/legal" element={<LegalSettings />} />

                </Routes>
            </main>
        </div>
    );
}

function Overview() {
    const [stats, setStats] = React.useState({
        doctors: 0,
        coordinators: 0,
        guilds: 0,
        referrals: 0
    });
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        async function fetchStats() {
            try {
                const [docs, coords, guilds, refs] = await Promise.all([
                    getAllDoctors(),
                    getAllCoordinators(),
                    getAllGuilds(),
                    getAllReferrals()
                ]);
                setStats({
                    doctors: docs.length,
                    coordinators: coords.length,
                    guilds: guilds.length,
                    referrals: refs.length
                });
            } catch (error) {
                console.error('Error fetching dashboard stats:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchStats();
    }, []);

    return (
        <div className="page-container">
            <h1>Dashboard Overview</h1>
            <div className={`stats-grid ${loading ? 'stats-loading' : ''}`}>
                <div className="stat-card">
                    <h3>Total Doctors</h3>
                    <p className="stat-value">{stats.doctors}</p>
                </div>
                <div className="stat-card">
                    <h3>Total Coordinators</h3>
                    <p className="stat-value">{stats.coordinators}</p>
                </div>
                <div className="stat-card">
                    <h3>Total Guilds</h3>
                    <p className="stat-value">{stats.guilds}</p>
                </div>
                <div className="stat-card">
                    <h3>Total Referrals</h3>
                    <p className="stat-value">{stats.referrals}</p>
                </div>
            </div>
        </div>
    );
}
